/**
 * The row of tools every editable plan screen carries.
 *
 * Shared between the list, the chart and the sheet, because the operations are the
 * same operations. A tool where indent means one thing on one tab and something
 * slightly different on the next is a tool people stop trusting with structure.
 *
 * Icons, with the words in the tooltip. Eight buttons of prose took up the width a
 * chart needs, and the icons are the ones every planner since Project has used for the
 * same eight actions, so nobody is learning a new language.
 */
import { useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import clsx from 'clsx'
import {
  AddIcon,
  DownIcon,
  IndentIcon,
  LinkIcon,
  OutdentIcon,
  TrashIcon,
  UnlinkIcon,
  UpIcon,
} from '../../components/icons'
import type { Row } from './plan'

/* ---------------------------------------------------------------------- toolbar */

export function PlanToolbar({
  selected,
  count = selected ? 1 : 0,
  rows,
  index,
  onAdd,
  canAdd = true,
  onIndent,
  onOutdent,
  onUp,
  onDown,
  onDelete,
  canDelete = true,
  onLink,
  onUnlink,
}: {
  selected: string | null
  /** How many rows are selected, when the screen allows more than one. */
  count?: number
  rows: Row[]
  index: number
  onAdd: () => void
  /**
   * Whether new work may be put on this plan at all — asked by SIDE rather than by capability,
   * because a customer's lead holds `work.write` at full. Their route in is "Report it", which
   * asks what a blank row does not.
   */
  canAdd?: boolean
  onIndent: () => void
  onOutdent: () => void
  onUp: () => void
  onDown: () => void
  onDelete: () => void
  /**
   * Whether every selected row may actually go — `work.delete`, which our people hold and
   * neither client role does. The button was gated on "is something selected", so a customer
   * who could not delete from the menu could still delete from here.
   */
  canDelete?: boolean
  /** Open the panel that says when this task is allowed to happen. */
  /** Link the selected tasks top to bottom. Absent when fewer than two are selected. */
  onLink?: () => void
  /** Remove every link into the selected tasks. Absent when there are none. */
  onUnlink?: () => void
}) {
  const has = Boolean(selected)
  return (
    <div className="flex flex-wrap items-center gap-1">
      {canAdd && (
        <>
          <Tool label="Add a task" onClick={onAdd} primary>
            <AddIcon />
          </Tool>
          <Divider />
        </>
      )}
      <Tool label="Indent (Tab)" disabled={!has || index <= 0} onClick={onIndent}>
        <IndentIcon />
      </Tool>
      <Tool label="Outdent (Shift+Tab)" disabled={!has} onClick={onOutdent}>
        <OutdentIcon />
      </Tool>
      <Tool label="Move up" disabled={!has || index <= 0} onClick={onUp}>
        <UpIcon />
      </Tool>
      <Tool label="Move down" disabled={!has || index >= rows.length - 1} onClick={onDown}>
        <DownIcon />
      </Tool>
      {(onLink !== undefined || onUnlink !== undefined || true) && (
        <>
          <Divider />
          <Tool label="Link the selected tasks, top to bottom, finish to start — select two or more with Ctrl or Shift" disabled={!onLink} onClick={onLink}>
            <LinkIcon />
          </Tool>
          <Tool label="Stop the selected tasks waiting for anything" disabled={!onUnlink} onClick={onUnlink}>
            <UnlinkIcon />
          </Tool>
        </>
      )}
      <Divider />
      {/*
        ABSENT, not greyed, when it is not theirs to do — the same call the row menu makes. A
        disabled bin invites "why not", and the answer is not something a tooltip can carry.
      */}
      {canDelete && (
        <Tool label={count > 1 ? `Delete ${count} tasks — this cannot be undone` : 'Delete — this one cannot be undone'} disabled={!has} onClick={onDelete} danger>
          <TrashIcon />
        </Tool>
      )}
      <span className="ml-2 text-[11px] text-[var(--color-ink-dim)]">
        {count > 1 ? `${count} selected` : has ? selected : 'Pick a row · Ctrl-click or Shift-click for several · right-click for more'}
      </span>
    </div>
  )
}

/**
 * A tooltip that appears the moment you hover, not a second later.
 *
 * The browser's own `title` tooltip takes about a second to show and looks like a
 * system dialog from another decade. On a toolbar of icons, where the tooltip *is* the
 * label, that second is the difference between a toolbar people can read and one they
 * have to learn. Shown on focus too, so the keyboard gets the same answer.
 */
export function Tip({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  const anchor = useRef<HTMLSpanElement>(null)
  const [at, setAt] = useState<{ top: number; left: number } | null>(null)

  /**
   * Measured and placed on the body, rather than hung under the button with CSS.
   *
   * `.plan-root` is `overflow: hidden` — it has to be, it contains the chart's own scrolling —
   * so a tooltip positioned inside it was cut off by whichever edge it reached. That was not
   * one unlucky button: eight of them were clipped, at BOTH ends of the toolbar. The tools on
   * the left lost their opening words and the ones on the right lost their closing ones, which
   * on a toolbar of icons means the label is the thing you cannot read.
   *
   * So it is measured on hover and rendered through a portal, the same answer the notification
   * panel reached for the same reason. Kept inside the window with a clamp, because the
   * furthest-right tool would otherwise put a 260px label half outside it.
   */
  /**
   * NOT WHILE SOMEBODY IS TYPING IN HERE.
   *
   * The tooltip shows on focus so the keyboard gets the same label the mouse does, which is
   * right for a toolbar of icons — the tip IS the label. The filter box is inside one of these
   * too, and there it was wrong: clicking into it opened a 260px label pinned directly under
   * the box, over the top of the grid, and it stayed there for as long as the field had focus,
   * which is the whole time you are typing a filter and reading what it matched.
   *
   * A field that carries its own placeholder does not need naming, so focus on one shows
   * nothing. Hovering it still explains what the filter matches, and moving the mouse away
   * clears it as before.
   */
  const typingInside = (target: EventTarget | null): boolean => {
    if (!(target instanceof HTMLElement)) return false
    return target.matches('input, textarea, select, [contenteditable="true"]')
  }

  const show = (event?: { target: EventTarget | null }) => {
    if (event && typingInside(event.target)) return
    /**
     * Hover is not enough on its own. The filter box is a field inside one of these, and
     * clicking into it leaves the pointer sitting on it — so the tip stayed up, over the row
     * of tools below, for the whole time somebody was typing and reading what matched. Once a
     * field in here has focus, the tip has said everything it usefully can.
     */
    if (anchor.current?.contains(document.activeElement) && typingInside(document.activeElement)) return
    const box = anchor.current?.getBoundingClientRect()
    if (!box) return
    const half = TIP_MAX / 2
    const middle = box.left + box.width / 2
    const left = Math.min(Math.max(middle, TIP_EDGE + half), window.innerWidth - TIP_EDGE - half)
    setAt({ top: box.bottom + 6, left })
  }

  return (
    <span
      ref={anchor}
      className={clsx('relative inline-flex', className)}
      onPointerEnter={() => show()}
      onPointerLeave={() => setAt(null)}
      onFocusCapture={(event) => (typingInside(event.target) ? setAt(null) : show(event))}
      onBlurCapture={() => setAt(null)}
    >
      {children}
      {at &&
        createPortal(
          <span
            role="tooltip"
            style={{ top: at.top, left: at.left, maxWidth: TIP_MAX }}
            className={clsx(
              'pointer-events-none fixed z-[999] -translate-x-1/2 rounded-[5px] px-2 py-1',
              'text-left text-[11px] leading-snug whitespace-normal shadow-lg',
              'bg-[var(--color-ink)] text-[var(--color-surface)]',
              'w-max',
            )}
          >
            {label}
          </span>,
          document.body,
        )}
    </span>
  )
}

/** The widest a tooltip goes, and how close to the window edge it may sit. */
const TIP_MAX = 260
const TIP_EDGE = 8

/** One icon button. The label is the tooltip and what a screen reader says. */
export function Tool({
  label,
  onClick,
  disabled = false,
  primary = false,
  danger = false,
  active = false,
  children,
}: {
  label: string
  onClick?: () => void
  disabled?: boolean
  primary?: boolean
  danger?: boolean
  /** A toggle that is currently on. */
  active?: boolean
  children: ReactNode
}) {
  return (
    <Tip label={label}>
      <button
        type="button"
        aria-label={label}
        aria-pressed={active || undefined}
        disabled={disabled}
        onClick={onClick}
        className={clsx(
          'inline-flex size-7 items-center justify-center rounded-[5px] border text-[var(--color-ink)] transition-colors',
          'disabled:cursor-not-allowed disabled:opacity-40',
          primary
            ? 'border-transparent bg-[var(--color-accent)] text-[var(--color-on-accent,white)] hover:opacity-90'
            : active
              ? 'border-[var(--color-accent)] bg-[var(--color-accent-soft)] text-[var(--color-accent-ink)]'
              : 'border-[var(--color-rule)] bg-[var(--color-surface)] hover:bg-[var(--color-surface-sunk)]',
          danger && !disabled && 'text-[var(--color-danger)] hover:border-[var(--color-danger)]',
        )}
      >
        {children}
      </button>
    </Tip>
  )
}

export function Divider() {
  return <span aria-hidden className="mx-1 h-4 w-px bg-[var(--color-rule)]" />
}
